import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';

// Services
import { getTechnicians, updateTechnician } from '../services/technicianService';

// Components
import TechnicianForm from './TechnicianForm';

const TechnicianManagement = ({ warehouseId, warehouseName }) => {
  const [technicians, setTechnicians] = useState([]);
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [technicianToAssign, setTechnicianToAssign] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Fetch technicians for this warehouse
  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all technicians
      const allTechsData = await getTechnicians({ include_inactive: true });
      setAllTechnicians(allTechsData);

      // Filter technicians assigned to this warehouse
      const warehouseTechnicians = allTechsData.filter(
        tech => tech.warehouse_id === warehouseId
      );

      setTechnicians(warehouseTechnicians);
    } catch (err) {
      console.error('Error fetching technicians:', err);
      setError('Failed to load technicians');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warehouseId) {
      fetchTechnicians();
    }
  }, [warehouseId]);

  // Handle form open/close
  const handleOpenForm = (technician = null) => {
    setSelectedTechnician(technician);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedTechnician(null);
  };

  const handleFormSuccess = () => {
    fetchTechnicians();
  };

  // Handle remove technician from warehouse
  const handleRemoveFromWarehouse = async (technician) => {
    try {
      await updateTechnician(technician.id, { warehouse_id: null });
      fetchTechnicians();
    } catch (err) {
      console.error('Error removing technician from warehouse:', err);
      setError('Failed to remove technician from warehouse');
    }
  };

  // Handle menu operations
  const handleMenuClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCreateNew = () => {
    handleMenuClose();
    handleOpenForm();
  };

  const handleAssignExisting = () => {
    handleMenuClose();
    setAssignDialogOpen(true);
  };

  // Handle assign existing technician
  const handleAssignTechnician = async () => {
    if (!technicianToAssign) return;

    try {
      await updateTechnician(technicianToAssign, { warehouse_id: warehouseId });
      setAssignDialogOpen(false);
      setTechnicianToAssign('');
      fetchTechnicians();
    } catch (err) {
      console.error('Error assigning technician to warehouse:', err);
      setError('Failed to assign technician to warehouse');
    }
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setTechnicianToAssign('');
  };

  // Get available technicians (not assigned to any warehouse or inactive)
  const getAvailableTechnicians = () => {
    return allTechnicians.filter(tech =>
      tech.is_active && (!tech.warehouse_id || tech.warehouse_id !== warehouseId)
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get type chip
  const getTypeChip = (type) => {
    const color = type === 'technician' ? 'primary' : 'secondary';
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    return <Chip label={label} color={color} size="small" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Technicians & Installers
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={handleMenuClick}
          >
            Add Technician
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleCreateNew}>
              <PersonAddIcon sx={{ mr: 1 }} />
              Create New Technician
            </MenuItem>
            <MenuItem
              onClick={handleAssignExisting}
              disabled={getAvailableTechnicians().length === 0}
            >
              <AddIcon sx={{ mr: 1 }} />
              Assign Existing Technician
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    Loading technicians...
                  </TableCell>
                </TableRow>
              ) : technicians.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No technicians assigned to this warehouse
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                technicians.map((technician) => (
                  <TableRow
                    key={technician.id}
                    hover
                    sx={{
                      ...(!technician.is_active && {
                        bgcolor: 'action.hover',
                        '& td': { color: 'text.disabled' }
                      })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            bgcolor: technician.is_active ? 'primary.main' : 'grey.500',
                            fontSize: '0.75rem'
                          }}
                        >
                          {technician.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {technician.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getTypeChip(technician.type)}</TableCell>
                    <TableCell>
                      <Chip
                        label={technician.is_active ? "Active" : "Inactive"}
                        color={technician.is_active ? "success" : "default"}
                        size="small"
                        variant={technician.is_active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>{formatDate(technician.created_at)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit Technician">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenForm(technician)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from Warehouse">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFromWarehouse(technician)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Technician Form Dialog */}
      <TechnicianForm
        open={formOpen}
        onClose={handleCloseForm}
        technician={selectedTechnician ? { ...selectedTechnician, warehouse_id: warehouseId } : { warehouse_id: warehouseId }}
        onSuccess={handleFormSuccess}
      />

      {/* Assign Existing Technician Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Existing Technician</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Technician</InputLabel>
              <Select
                value={technicianToAssign}
                onChange={(e) => setTechnicianToAssign(e.target.value)}
                label="Select Technician"
              >
                {getAvailableTechnicians().map(tech => (
                  <MenuItem key={tech.id} value={tech.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                        {tech.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{tech.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tech.type.charAt(0).toUpperCase() + tech.type.slice(1)}
                          {tech.warehouse && ` • Currently at ${tech.warehouse.name}`}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button
            onClick={handleAssignTechnician}
            variant="contained"
            disabled={!technicianToAssign}
          >
            Assign to Warehouse
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianManagement;
